import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { ArrowUpRight, X } from 'lucide-react'
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'

import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { onIntroReveal } from '@/lib/intro'
import { Logo } from '@/components/generals/logo'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import { TransitionLink } from '@/components/generals/transition-link'

import {
    FULL_MOTION,
    gsap,
    REDUCED_MOTION,
    ScrollTrigger,
    useGSAP
} from '@/lib/gsap'

import {
    BOOKING_HREF,
    contact,
    fullNav,
    openingHours,
    primaryNav
} from '@/constants/site'

export const Header: FC = (): ReactNode => {
    const headerRef = useRef<HTMLElement>(null)
    const barRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const toggleRef = useRef<HTMLButtonElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const menuTimeline = useRef<gsap.core.Timeline | null>(null)
    const lockedRef = useRef(false)

    const [solid, setSolid] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    const { pathname } = useRouter()
    const { lock, unlock } = useSmoothScroll()

    useGSAP(() => {
        const update = (scroll: number, direction: number): void => {
            const threshold = window.innerHeight * 0.75
            setSolid(scroll > threshold)
            setHidden(scroll > threshold + 160 && direction === 1)
        }

        const trigger = ScrollTrigger.create({
            start: 0,
            end: 'max',
            onUpdate: (self) => update(self.scroll(), self.direction),
            onRefresh: (self) => update(self.scroll(), -1)
        })

        return () => trigger.kill()
    })

    useGSAP(() => {
        const bar = barRef.current
        if (!bar) return

        const mm = gsap.matchMedia()

        mm.add(FULL_MOTION, () => {
            let entrance: gsap.core.Tween | undefined

            const unsubscribe = onIntroReveal(() => {
                entrance = gsap.from(bar.children, {
                    yPercent: -140,
                    autoAlpha: 0,
                    duration: 1.4,
                    ease: 'expo.out',
                    stagger: 0.08,
                    delay: 0.6
                })
            })

            return () => {
                unsubscribe()
                entrance?.revert()
            }
        })

        return () => mm.revert()
    }, { scope: headerRef })

    useGSAP(() => {
        const menu = menuRef.current
        if (!menu) return

        const links = menu.querySelectorAll('[data-menu-link]')
        const fades = menu.querySelectorAll('[data-menu-fade]')

        const timeline = gsap.timeline({ paused: true })

        timeline
            .set(menu, { visibility: 'visible' })
            .fromTo(menu, { clipPath: 'inset(0% 0% 100% 0%)' }, {
                clipPath: 'inset(0% 0% 0% 0%)',
                duration: 0.95,
                ease: 'expo.inOut'
            })
            .fromTo(links, { yPercent: 110 }, {
                yPercent: 0,
                duration: 1,
                stagger: 0.035,
                ease: 'expo.out'
            }, 0.4)
            .fromTo(fades, { autoAlpha: 0, y: 18 }, {
                autoAlpha: 1,
                y: 0,
                duration: 0.9,
                stagger: 0.07,
                ease: 'power3.out'
            }, 0.55)

        menuTimeline.current = timeline

        return () => {
            menuTimeline.current = null
        }
    }, { scope: menuRef })

    const openMenu = useCallback((): void => {
        setMenuOpen(true)
        lockedRef.current = true
        lock()

        const timeline = menuTimeline.current
        const reduceMotion = window.matchMedia(REDUCED_MOTION).matches

        if (timeline) {
            if (reduceMotion) timeline.progress(1).pause()
            else timeline.timeScale(1).play()
        }

        window.setTimeout(() => closeRef.current?.focus(), 60)
    }, [lock])

    const closeMenu = useCallback((restoreFocus = true): void => {
        setMenuOpen(false)

        if (lockedRef.current) {
            lockedRef.current = false
            unlock()
        }

        const timeline = menuTimeline.current
        const reduceMotion = window.matchMedia(REDUCED_MOTION).matches

        if (timeline) {
            if (reduceMotion) timeline.progress(0).pause()
            else timeline.timeScale(1.7).reverse()
        }

        if (restoreFocus) toggleRef.current?.focus()
    }, [unlock])

    useEffect(() => {
        if (!menuOpen) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.preventDefault()
                closeMenu()
                return
            }

            if (event.key !== 'Tab' || !menuRef.current) return

            const focusable = Array.from(menuRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))
            if (focusable.length === 0) return

            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [menuOpen, closeMenu])

    // Leaving the page with the menu open (for example with the back button) must release the scroll lock.
    useEffect(() => {
        return () => {
            if (lockedRef.current) {
                lockedRef.current = false
                unlock()
            }
        }
    }, [unlock])

    const leaveMenu = (): void => {
        if (menuOpen) closeMenu(false)
    }

    const isCurrent = (href: string): boolean => pathname === href

    return (
        <>
            <header
                ref={headerRef}
                className={cn(
                    'fixed inset-x-0 top-0 z-50 border-b transition-[translate,background-color,color,border-color] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]',
                    solid ? 'border-charcoal/10 bg-bone/85 text-charcoal backdrop-blur-xl' : 'border-transparent bg-transparent text-bone',
                    hidden && !menuOpen ? '-translate-y-full' : 'translate-y-0'
                )}
            >
                <a
                    href='#main'
                    className='sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[70] focus:rounded-full focus:bg-bone focus:px-4 focus:py-2 focus:text-sm focus:text-charcoal'
                >
                    Skip to content
                </a>

                <div ref={barRef} className='flex h-16 items-center justify-between gap-6 px-5 sm:px-8 lg:h-[72px] lg:px-12'>
                    <TransitionLink
                        href='/'
                        aria-label='Morrow Golf Park, home'
                        className='rounded-full outline-offset-4'
                    >
                        <Logo />
                    </TransitionLink>

                    <nav aria-label='Primary' className='hidden xl:block'>
                        <ul className='flex items-center gap-7 xl:gap-9'>
                            {primaryNav.map((link) => (
                                <li key={link.href}>
                                    <TransitionLink
                                        href={link.href}
                                        aria-current={isCurrent(link.href) ? 'page' : undefined}
                                        className='group relative py-2 text-[12px] font-medium uppercase tracking-[0.16em]'
                                    >
                                        {link.label}

                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'absolute inset-x-0 bottom-0.5 h-px bg-current transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
                                                isCurrent(link.href)
                                                    ? 'scale-x-100'
                                                    : 'origin-right scale-x-0 group-hover:origin-left group-hover:scale-x-100'
                                            )}
                                        />
                                    </TransitionLink>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-2 sm:gap-3'>
                        <TransitionLink
                            href={BOOKING_HREF}
                            className={cn(
                                'inline-flex h-10 items-center gap-2 rounded-full px-4 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-500 sm:px-5',
                                solid ? 'bg-forest text-bone hover:bg-pine' : 'bg-bone text-charcoal hover:bg-white'
                            )}
                        >
                            <span className='sm:hidden'>
                                Book
                            </span>

                            <span className='hidden sm:inline'>
                                Book a tee time
                            </span>
                        </TransitionLink>

                        <button
                            ref={toggleRef}
                            type='button'
                            onClick={openMenu}
                            aria-expanded={menuOpen}
                            aria-controls='site-menu'
                            className='group flex size-10 items-center justify-center rounded-full border border-current/25 transition-colors hover:border-current/60'
                        >
                            <span className='sr-only'>
                                Open menu
                            </span>

                            <span aria-hidden='true' className='flex w-4 flex-col gap-[5px]'>
                                <span className='h-px w-full bg-current transition-transform duration-500 group-hover:-translate-y-px' />
                                <span className='h-px w-2/3 self-end bg-current transition-[width] duration-500 group-hover:w-full' />
                            </span>
                        </button>
                    </div>
                </div>
            </header>

            <div
                ref={menuRef}
                id='site-menu'
                role='dialog'
                aria-modal='true'
                aria-label='Site menu'
                inert={!menuOpen}
                className='invisible fixed inset-0 z-[60] flex flex-col bg-pine text-bone'
                style={{ clipPath: 'inset(0% 0% 100% 0%)' }}
            >
                <div className='flex h-16 shrink-0 items-center justify-between px-5 sm:px-8 lg:h-[72px] lg:px-12'>
                    <Logo />

                    <button
                        ref={closeRef}
                        type='button'
                        onClick={() => closeMenu()}
                        className='flex h-10 items-center gap-2 rounded-full border border-bone/25 pl-4 pr-3 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors hover:border-bone/60'
                    >
                        Close
                        <X className='size-4' aria-hidden='true' />
                    </button>
                </div>

                <div data-lenis-prevent className='flex-1 overflow-y-auto px-5 pb-12 sm:px-8 lg:px-12'>
                    <div className='grid gap-12 pt-6 lg:grid-cols-12 lg:gap-8 lg:pt-12'>
                        <nav aria-label='All pages' className='lg:col-span-8'>
                            <ol className='grid gap-x-10 sm:grid-cols-2'>
                                {fullNav.map((link, index) => (
                                    <li key={link.href} className='border-b border-bone/10'>
                                        <TransitionLink
                                            href={link.href}
                                            onClick={leaveMenu}
                                            aria-current={isCurrent(link.href) ? 'page' : undefined}
                                            className='group block overflow-hidden py-3.5 lg:py-4'
                                        >
                                            <span data-menu-link className='flex items-baseline gap-4'>
                                                <span className='font-mono text-[11px] tabular-nums text-bone/45'>
                                                    {pad(index)}
                                                </span>

                                                <span
                                                    className={cn(
                                                        'type-wide text-[clamp(1.3rem,2.4vw,2.25rem)] font-medium uppercase leading-tight tracking-[-0.01em] transition-colors duration-300 group-hover:text-ochre',
                                                        isCurrent(link.href) && 'text-ochre'
                                                    )}
                                                >
                                                    {link.label}
                                                </span>
                                            </span>
                                        </TransitionLink>
                                    </li>
                                ))}
                            </ol>
                        </nav>

                        <aside className='space-y-9 lg:col-span-4 lg:pl-8'>
                            <div data-menu-fade>
                                <p className='mb-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/50'>
                                    Visit
                                </p>

                                <address className='not-italic leading-relaxed text-bone/85'>
                                    {contact.addressLines.map((line) => (
                                        <span key={line} className='block'>
                                            {line}
                                        </span>
                                    ))}
                                </address>
                            </div>

                            <div data-menu-fade>
                                <p className='mb-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/50'>
                                    Hours
                                </p>

                                <dl className='space-y-1.5 text-bone/85'>
                                    {openingHours.map((item) => (
                                        <div key={item.label} className='flex justify-between gap-6'>
                                            <dt>
                                                {item.label}
                                            </dt>

                                            <dd className='text-bone/60'>
                                                {item.value}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>

                            <div data-menu-fade>
                                <p className='mb-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/50'>
                                    Concierge
                                </p>

                                <a href={`mailto:${contact.email}`} className='text-bone/85 underline decoration-bone/30 underline-offset-4 transition-colors hover:text-ochre'>
                                    {contact.email}
                                </a>
                            </div>

                            <TransitionLink
                                data-menu-fade
                                href={BOOKING_HREF}
                                onClick={leaveMenu}
                                className='inline-flex h-12 items-center gap-3 rounded-full bg-bone px-6 text-[12px] font-semibold uppercase tracking-[0.16em] text-charcoal transition-colors hover:bg-ochre'
                            >
                                Book a tee time
                                <ArrowUpRight className='size-4' aria-hidden='true' />
                            </TransitionLink>
                        </aside>
                    </div>
                </div>
            </div>
        </>
    )
}
